/**
 * PackLandingTemplate.module.scss — Toile.
 *
 * The page shell copied from `@product/narcis/packs/PackLandingTemplate.module.scss`: the hero's
 * 12-column grid with the sticky 5-column aside and the 7-column stage, the mobile header hoist and
 * its desktop/mobile visibility pair, the gallery-proof band, the SEO block. Everything that styled
 * a state Toile does not have came out with it — price rows, queue chips and ghosts, CTA and its
 * gates, train card, workbench/uploader/slots/preview panels, price bar, dialogs, how-it-works,
 * returning-buyer banner, legal note, delivered-view leftovers. The chips row and the per-cell
 * recipe block that used to occupy the CTA's slot are also gone (pack page canon convergence,
 * 2026-09-01) — narcis/ovid carry neither. The related strip came back in its own module
 * (`PackRelatedStrip.module.scss`), matching canon: it renders independent of any purchase state.
 *
 * Retokened onto `--tl-*`. Narcis's mixin imports (`@styles/fonts.scss` et al. resolve to narcis's
 * own partials) are dropped: Toile sets one face and states it, which is what its other route
 * modules do.
 *
 * Breakpoints are narcis's set, unchanged — 1023 (mobile header hoist), 899 (hero collapses to one
 * column, aside reorders below the stage), 900/600 (padding steps), 639 (cells single-column, strip
 * scrolls; in PackGrid/PackHeroStage).
 */

.page {
    min-height: 100vh;
    min-height: 100dvh;
    display: flex;
    flex-direction: column;
    background: var(--tl-stage);
}

/* ── Detail hero ─────────────────────────────────────────────────────────────── */

.detailHero {
    /* The shared stage pieces (`core/packs/stage/`: the coda, its thumb, the two doors) read these
       and nothing else — Toile's tokens, set once for the stage and the aside together. */
    --stage-accent: var(--tl-accent);
    --stage-ground: var(--tl-stage);
    --stage-text: var(--tl-text);
    --stage-muted: var(--tl-muted);
    --stage-line: var(--tl-border);
    --stage-radius: 16px;

    padding: 40px 26px 28px;
    max-width: 1200px;
    width: 100%;
    margin: 0 auto;
    box-sizing: border-box;

    @media (max-width: 900px) {
        padding: 32px 26px 22px;
    }

    @media (max-width: 600px) {
        padding: 26px 18px 18px;
    }
}

.detailHero__grid {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    gap: 32px;
    align-items: start;

    @media (max-width: 899px) {
        grid-template-columns: 1fr;
        gap: 24px;
    }
}

.detailHero__left {
    grid-column: span 5;
    position: sticky;
    /* The header is 49px tall and sticky; this clears it with the same breathing room narcis
       gives its own 56px bar. */
    top: 72px;
    align-self: start;
    min-width: 0;

    @media (max-width: 899px) {
        grid-column: 1 / -1;
        position: static;
        /* The aside renders below the stage on one column: the phone reads eyebrow → H1 (hoisted
           above the grid) → stage → thumbs → recipe → grid. */
        order: 2;
        /* The phone's first screen owes the reader the picture and the verb; the tagline opens the
           body below. The left column becomes a flex column here so the tagline can sink under
           the aside (order: 1, set on .detailHero__tagline) while the source order stays prose. */
        display: flex;
        flex-direction: column;
        /* A grid item's automatic min-width is content-based, not 0. The thumb strip below 640
           holds nine non-shrinking 44px thumbs (444px of intrinsic content); without min-width:0
           on BOTH tracks that content forces the single 1fr track wider than the viewport and the
           whole document scrolls horizontally. narcis carried this fix from a live phone report. */
        min-width: 0;
    }
}

.detailHero__right {
    grid-column: span 7;
    display: flex;
    flex-direction: column;
    gap: 24px;
    align-items: center;
    min-width: 0;

    @media (max-width: 899px) {
        grid-column: 1 / -1;
        order: 1;
        align-items: stretch;
        /* Same fix as the aside, same root cause — and this is the column that actually carries
           the offending subtree. */
        min-width: 0;
    }
}

/* Mobile-only eyebrow + title block: visible only below 1024, above the grid. The in-aside copies
   are hidden there, so exactly one H1 renders at any width. */
.detailHero__mobileHeader {
    display: none;

    @media (max-width: 1023px) {
        display: block;
        margin-bottom: 16px;
    }
}

.detailHero__desktopOnly {
    @media (max-width: 1023px) {
        display: none;
    }
}

.detailHero__eyebrow {
    font-family: 'JetBrains Mono', monospace;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    font-size: 10.5px;
    color: var(--tl-muted);
    margin: 0 0 12px;
}

.detailHero__heading {
    font-weight: 500;
    font-size: 40px;
    line-height: 1.1;
    letter-spacing: -0.02em;
    color: var(--tl-text);
    margin: 0 0 14px;

    @media (max-width: 900px) {
        font-size: 32px;
    }

    @media (max-width: 600px) {
        font-size: 28px;
    }
}

.detailHero__tagline {
    @media (max-width: 899px) {
        order: 1;
        margin: 16px 0 0;
    }
    font-size: 15px;
    line-height: 1.55;
    color: var(--tl-muted);
    margin: 0 0 16px;
}

/* ── Gallery proof — the nine cells below the hero ───────────────────────────── */

.galleryProof {
    padding: 28px 26px 0;
    max-width: 1200px;
    width: 100%;
    margin: 0 auto;
    box-sizing: border-box;
    border-top: 1px solid var(--tl-border);
    overflow: hidden;

    @media (max-width: 900px) {
        padding: 24px 26px 0;
    }

    @media (max-width: 600px) {
        padding: 20px 18px 0;
    }
}

.galleryProofInner {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

/* ── SEO block ───────────────────────────────────────────────────────────────── */

.seoContent {
    width: 100%;
    max-width: 720px;
    margin: 0 auto;
    padding: 0 26px;
    box-sizing: border-box;
}

.seoSection {
    border-top: 1px solid var(--tl-border);
    padding: 3rem 0;
}

.seoHeading {
    font-weight: 500;
    font-size: 22px;
    letter-spacing: -0.01em;
    color: var(--tl-text);
    margin: 0 0 1.5rem;

    @media (max-width: 600px) {
        font-size: 19px;
    }
}

.seoFaqList {
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

.seoFaqItem {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
}

.seoFaqQ {
    font-size: 15px;
    font-weight: 500;
    color: var(--tl-text);
    margin: 0;
}

.seoFaqA {
    font-size: 14px;
    line-height: 1.7;
    color: var(--tl-muted);
    margin: 0;
}

.seoParagraph {
    font-size: 15px;
    line-height: 1.75;
    color: var(--tl-muted);
    margin: 0 0 1.25rem;
}

.seoUseCasesList {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 0.625rem;

    li {
        font-size: 14px;
        line-height: 1.6;
        color: var(--tl-muted);
        padding-left: 1.25rem;
        position: relative;

        &::before {
            content: '—';
            position: absolute;
            left: 0;
            color: var(--tl-text);
        }
    }
}
